import { DateTime } from "luxon";
import { HolidayCountry } from "../enums/holiday-country.enum";
import { DateHelpers } from "../helpers/date-helpers";
import { HolidayModel } from "./holiday.model";

export class HolidayManager {

    private holidays: Array<HolidayModel> = [];

    private holidaysDict: Record<string, Record<HolidayCountry, HolidayModel>> = {};

    private readonly dateStorageFormat = "yyyy-mm-dd";

    private static instance: HolidayManager | null = null;

    constructor() {}

    public static getInstance(): HolidayManager {
        if (this.instance == null) {
            this.instance = new HolidayManager();
        }

        return this.instance;
    }

    public static setHolidays(holidaysData: Array<HolidayModel>) {
        if (!holidaysData) {
            return;
        }

        const instance = this.getInstance();
        instance.holidays = holidaysData;
        
        for (const holiday of instance.holidays) {
            if (!holiday.date) {
                continue;
            }
            
            const ymd = DateHelpers.format(instance.dateStorageFormat, holiday.date);
            
            let holidayForCountry: Record<HolidayCountry, HolidayModel> = {} as any;

            if (instance.holidaysDict[ymd]) {
                holidayForCountry = instance.holidaysDict[ymd];
            }

            holidayForCountry[holiday.country] = holiday;
            instance.holidaysDict[ymd] = holidayForCountry;
        }
    }

    public getHolidayCountry(exchangeCode: string): HolidayCountry {
        if (exchangeCode == "CAT" || exchangeCode == "CAV")
            return HolidayCountry.Canada;
        return HolidayCountry.US;
    }

    public isHalfDayWithReferenceDate(referenceDate?: Date): boolean {
        if (!referenceDate)
            referenceDate = new Date();

        const country = HolidayCountry.US;
        return this.isHalfDayWithCountry(country, referenceDate);
    }

    public isHalfDayWithExchangeCode(exchangeCode: string, date: Date): boolean {
        const country = this.getHolidayCountry(exchangeCode);
        return this.isHalfDayWithCountry(country, date);
    }

    public isHalfDayWithCountry(country: HolidayCountry, date: Date): boolean {
        const ymd = DateHelpers.format(this.dateStorageFormat, date);
        if (this.holidaysDict[ymd] && this.holidaysDict[ymd][country])
            return this.holidaysDict[ymd][country].halfDay;

        return false;
    }

    public isHolidayMarketClosed(referenceDate?: Date): boolean {
        if (!referenceDate) {
            referenceDate = new Date();
        }
        const country = HolidayCountry.US;
        return this.isHolidayMarketClosedWithCountry(country, referenceDate);
    }
 
    public isHolidayMarketClosedWithExchangeCode(exchangeCode: string, date: Date): boolean {
        const country = this.getHolidayCountry(exchangeCode);
        return this.isHolidayMarketClosedWithCountry(country, date);
    }

    public isHolidayMarketClosedWithCountry(country: HolidayCountry, date: Date): boolean {
        const ymd = DateHelpers.format(this.dateStorageFormat, date);
        if (this.holidaysDict[ymd] && this.holidaysDict[ymd][country])
            return this.holidaysDict[ymd][country].closed;

        return false;
    }
    
    public isMarketClosed(referenceDate?: Date): boolean {
        if (!referenceDate) {
            referenceDate = new Date();
        }

        return this.isHolidayMarketClosed(referenceDate) || DateHelpers.isWeekend(referenceDate);
    }

    public getMarketOpenInMarketTimeZone(date: Date): DateTime {
        let dateInMarketTimeZone = DateHelpers.toMarketTimeZone(date);

        // TraceLoggingHelpers.log('dateInMarketTimeZone ', dateInMarketTimeZone)
        // Find next marketday.  Take into account weekends and holidays.
        dateInMarketTimeZone = DateHelpers.getNextWeekday(dateInMarketTimeZone);

        const holidayManager = HolidayManager.getInstance();
        while (holidayManager.isHolidayMarketClosed(dateInMarketTimeZone.toJSDate())) {
            // Find next marketday.  Take into account weekends and holidays.
            dateInMarketTimeZone = DateHelpers.getNextWeekday(dateInMarketTimeZone);
            dateInMarketTimeZone = DateHelpers.addWeekDays(dateInMarketTimeZone, 1);
        }

        const marketOpenMarketTimeZone = dateInMarketTimeZone.set({hour: 9, minute: 30, second: 0, millisecond: 0});
        // TraceLoggingHelpers.log('marketOpenMarketTimeZone ', marketOpenMarketTimeZone)
        return marketOpenMarketTimeZone;
    }

    public getMarketCloseInMarketTimeZone(date: Date): DateTime {
        let dateInMarketTimeZone = DateHelpers.toMarketTimeZone(date);
        let minutesTillTodaysClose = 16 * 60;
    
        const holidayManager = HolidayManager.getInstance();
        if (holidayManager.isHalfDayWithReferenceDate(date))  {
            minutesTillTodaysClose = 13 * 60;
        }

        dateInMarketTimeZone = dateInMarketTimeZone.set({hour: 0, minute: 0, second: 0, millisecond: 0});
        const marketCloseMarketTimeZone = dateInMarketTimeZone.plus({minutes: minutesTillTodaysClose});
        return marketCloseMarketTimeZone;
    }
}